/**
 * Résumés IA des échanges (L2, Dove 07/09/2026) — le contrat partagé.
 *
 * Un résumé par échange, quel que soit le canal. Le modèle rend `ResumeIaSchema`
 * (strict : une clé de trop = sortie rejetée, état « à vérifier »). Le CRM y
 * ajoute le rapprochement des patients et l'état des propositions. Une
 * proposition ne devient une action QUE par un clic humain (`actionId`).
 *
 * N'importe que `zod` et `./primitives` : `relances.ts` importe ce fichier,
 * l'inverse créerait un cycle.
 */
import { z } from "zod";
import { IsoDate, Uuid } from "./primitives";

export const CANAUX_RESUME = ["email", "whatsapp", "appel", "sms"] as const;
export type CanalResume = (typeof CANAUX_RESUME)[number];

export const ETATS_RESUME = ["generee", "a_verifier", "approuvee", "rejetee", "utilisee"] as const;
export type EtatResume = (typeof ETATS_RESUME)[number];

export const TYPES_PROPOSITION = ["tache", "rdv"] as const;

/** Ce que le modèle propose ; `actionId` est posé par le CRM au clic « Créer ». */
export const PropositionSchema = z.object({
  type: z.enum(TYPES_PROPOSITION),
  titre: z.string().min(1),
  quand: IsoDate.nullable(),
  heure: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable(),
  pour: z.enum(["labo", "cabinet"]),
  actionId: z.string().nullable().default(null),
});
export type Proposition = z.infer<typeof PropositionSchema>;

export const PatientCiteSchema = z.object({
  nom: z.string().min(1),
  patientId: z.string().nullable(),
  source: z.enum(["crm", "vevidental"]).nullable(),
  confiance: z.enum(["haute", "basse", "aucune"]),
});
export type PatientCite = z.infer<typeof PatientCiteSchema>;

/** La sortie BRUTE du modèle — strict. */
export const ResumeIaSchema = z
  .object({
    resume: z.string().min(1),
    patients: z.array(z.string()),
    propositions: z.array(PropositionSchema.omit({ actionId: true }).strict()),
    urgence: z.enum(["haute", "normale"]),
  })
  .strict();
export type ResumeIa = z.infer<typeof ResumeIaSchema>;

export const EchangeResumeSchema = z.object({
  id: z.string(),
  canal: z.enum(CANAUX_RESUME),
  sourceId: z.string(),
  cabinetId: z.string().nullable(),
  practitionerId: z.string().nullable(),
  resume: z.string().nullable(),
  patients: z.array(PatientCiteSchema),
  propositions: z.array(PropositionSchema),
  urgence: z.enum(["haute", "normale"]).nullable(),
  etat: z.enum(ETATS_RESUME),
  modele: z.string().nullable(),
  consigneVersion: z.string().nullable(),
  erreur: z.string().nullable(),
  genereLe: z.string().nullable(),
  decidePar: z.string().nullable(),
  decideLe: z.string().nullable(),
});
export type EchangeResume = z.infer<typeof EchangeResumeSchema>;

/** Explication d'une action née d'une proposition IA (file Relations). */
export const ECHANGE_RESUME_RULE_VERSION = "echange-resume/v1" as const;
export const EchangeResumeExplanationSchema = z.object({
  ruleVersion: z.literal(ECHANGE_RESUME_RULE_VERSION),
  canal: z.enum(CANAUX_RESUME),
  sourceId: z.string(),
  resumeId: Uuid,
  titre: z.string(),
  resume: z.string(),
  patientNom: z.string().nullable(),
  echeanceLe: IsoDate.nullable(),
  typeProposition: z.enum(TYPES_PROPOSITION),
});
export type EchangeResumeExplanation = z.infer<typeof EchangeResumeExplanationSchema>;

export const ETAT_RESUME_LIBELLE: Record<EtatResume, string> = {
  generee: "Résumé IA",
  a_verifier: "À vérifier",
  approuvee: "Approuvé",
  rejetee: "Rejeté",
  utilisee: "Utilisé",
};
